import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useFormik } from 'formik'

import { groupValidationSchema } from '../groupValidationSchema'

import { ConfirmationResultModal, Alert, FormField, TextInput, TextArea, Spacer, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { CreateGroupModalProps } from './CreateGroupModal.types'

type ModalState = 'confirm' | 'success' | 'failure'

export function CreateGroupModal({ isOpen, onClose, onSuccess }: CreateGroupModalProps) {
  const { t } = useTranslation()
  const { groupService } = useServices()
  const [modalState, setModalState] = useState<ModalState>('confirm')
  const [createdGroupName, setCreatedGroupName] = useState('')

  const [{ isPending }, { mutateAsync: createGroup }] = useMutation(groupService.createGroup)

  const formik = useFormik({
    initialValues: {
      name: '',
      description: ''
    },
    validationSchema: groupValidationSchema(t),
    onSubmit: async values => {
      try {
        await createGroup({
          name: values.name.trim(),
          ...(values.description.trim() && { description: values.description.trim() })
        })
        setCreatedGroupName(values.name.trim())
        setModalState('success')
      } catch {
        setCreatedGroupName(values.name.trim())
        setModalState('failure')
      }
    }
  })

  const handleClose = () => {
    if (!isPending) {
      formik.resetForm()
      setModalState('confirm')
      setCreatedGroupName('')
      onClose()
    }
  }

  const handleSuccessClose = () => {
    formik.resetForm()
    setModalState('confirm')
    setCreatedGroupName('')
    onSuccess?.()
    onClose()
  }

  const handleFailureClose = () => {
    setModalState('confirm')
  }

  if (!isOpen) {
    return null
  }

  const isSubmitDisabled = !formik.values.name.trim() || !formik.isValid || isPending

  return (
    <ConfirmationResultModal
      isOpen={isOpen}
      state={modalState === 'confirm' ? undefined : modalState}
      title={t('modal.createGroup.title')}
      primaryButtonText={isPending ? t('modal.createGroup.button.creating') : t('button.createGroup')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={formik.handleSubmit}
      onClose={modalState === 'success' ? handleSuccessClose : handleClose}
      onFailureClose={handleFailureClose}
      disabled={isSubmitDisabled}
      loading={isPending}
      primaryBtnTestId="create-group-submit-button"
      secondaryBtnTestId="create-group-cancel-button"
      success={{
        title: t('modal.createGroup.success.title'),
        description: (
          <Trans
            i18nKey="modal.createGroup.success.description"
            values={{ name: createdGroupName }}
            components={{ bold: <Text as="span" fontWeight="semiBold" /> }}
          />
        ),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.createGroup.error.title'),
        description: (
          <Trans
            i18nKey="modal.createGroup.error.description"
            values={{ name: createdGroupName }}
            components={{ bold: <Text as="span" fontWeight="semiBold" /> }}
          />
        ),
        button: t('button.close')
      }}
    >
      <Alert variant="info" title={t('modal.createGroup.alert')} />
      <Spacer size={6} axis="vertical" />
      <form>
        <FormField
          label={t('modal.createGroup.name.label')}
          status={formik.touched.name && formik.errors.name ? 'danger' : 'default'}
          statusMessage={formik.touched.name && formik.errors.name ? formik.errors.name : ''}
          necessity="required"
        >
          <TextInput
            name="name"
            placeholder={t('modal.createGroup.name.placeholder')}
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            data-testid="create-group-name-input"
          />
        </FormField>
        <Spacer size={4} axis="vertical" />
        <FormField
          label={t('modal.createGroup.description.label')}
          description={t('modal.createGroup.description.hint')}
        >
          <TextArea
            name="description"
            placeholder={t('modal.createGroup.description.placeholder')}
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            data-testid="create-group-description-input"
          />
        </FormField>
      </form>
    </ConfirmationResultModal>
  )
}
